import { formatEatDateTime } from '@wodi/domain';
import type { LabourObservationView, PartographLinesView } from '@wodi/shared';

import styles from './maternity.module.css';

// The partograph and growth charts (docs/plans/phase2-maternity-mch.md §4.3, §4.6): ink on
// paper, tokens only, entries as drawn points. Alert and action lines appear only when the
// facility has set them (dashed); the caption says "not set up" otherwise. Growth has no
// reference curves (M19). The tables beside the charts carry every value in words.

const WIDTH = 640;
const HEIGHT = 220;
const PAD = { top: 16, right: 16, bottom: 28, left: 48 };
const HOUR_MS = 3_600_000;

export interface Point {
  x: number;
  y: number;
}

/** Maps time and a value onto the chart box, oldest on the left. */
export function scale(input: {
  times: readonly number[];
  low: number;
  high: number;
  minSpanMs?: number;
}) {
  const first = Math.min(...input.times);
  const lastRaw = Math.max(...input.times);
  const last = Math.max(lastRaw, first + (input.minSpanMs ?? HOUR_MS));
  const innerW = WIDTH - PAD.left - PAD.right;
  const innerH = HEIGHT - PAD.top - PAD.bottom;
  return (t: number, v: number): Point => ({
    x: PAD.left + ((t - first) / (last - first)) * innerW,
    y: PAD.top + innerH - ((v - input.low) / (input.high - input.low)) * innerH,
  });
}

export interface PartographLayout {
  points: (Point & { id: string })[];
  alert: [Point, Point] | null;
  action: [Point, Point] | null;
}

/** The dilatation points and, when set up, the alert and action lines, on 0–10 cm. */
export function partographLayout(
  observations: readonly LabourObservationView[],
  lines: PartographLinesView,
): PartographLayout | null {
  const entries = observations
    .filter((o) => o.status === 'active' && o.cervicalDilationCm !== null)
    .map((o) => ({ id: o.id, t: Date.parse(o.observedAt), v: o.cervicalDilationCm ?? 0 }))
    .sort((a, b) => a.t - b.t);
  if (entries.length === 0) return null;
  const times = entries.map((e) => e.t);
  if (lines.state === 'drawn') {
    times.push(Date.parse(lines.action.end.at));
  }
  const at = scale({ times, low: 0, high: 10, minSpanMs: 4 * HOUR_MS });
  const line = (l: { start: { at: string; cm: number }; end: { at: string; cm: number } }) =>
    [at(Date.parse(l.start.at), l.start.cm), at(Date.parse(l.end.at), l.end.cm)] as [Point, Point];
  return {
    points: entries.map((e) => ({ id: e.id, ...at(e.t, e.v) })),
    alert: lines.state === 'drawn' ? line(lines.alert) : null,
    action: lines.state === 'drawn' ? line(lines.action) : null,
  };
}

function Axes({ low, high, unit }: { low: string; high: string; unit: string }) {
  return (
    <>
      <line
        className={styles.chartAxis}
        x1={PAD.left}
        x2={WIDTH - PAD.right}
        y1={HEIGHT - PAD.bottom}
        y2={HEIGHT - PAD.bottom}
      />
      <line
        className={styles.chartAxis}
        x1={PAD.left}
        x2={PAD.left}
        y1={PAD.top}
        y2={HEIGHT - PAD.bottom}
      />
      <text className={styles.chartLabel} x={PAD.left - 6} y={PAD.top + 4} textAnchor="end">
        {high}
      </text>
      <text className={styles.chartLabel} x={PAD.left - 6} y={HEIGHT - PAD.bottom} textAnchor="end">
        {low}
      </text>
      <text className={styles.chartLabel} x={PAD.left} y={HEIGHT - 6}>
        {unit}
      </text>
    </>
  );
}

export function PartographChart({
  observations,
  lines,
}: {
  observations: readonly LabourObservationView[];
  lines: PartographLinesView;
}) {
  const layout = partographLayout(observations, lines);
  const caption =
    lines.state === 'not_configured'
      ? 'Alert and action lines: not set up. The clinical lead sets them in Settings > Maternity and MCH.'
      : lines.state === 'waiting_for_start'
        ? 'Alert and action lines start when dilatation reaches the facility’s set starting point.'
        : 'Dashed: the alert line, then the action line, from the facility’s settings.';
  if (layout === null) {
    return (
      <>
        <p className={styles.meta}>No cervical dilatation entered yet.</p>
        <p className={styles.meta}>{caption}</p>
      </>
    );
  }
  return (
    <figure className={styles.form}>
      <svg
        viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
        className={styles.chart}
        role="img"
        aria-label={`Cervical dilatation over time: ${String(layout.points.length)} entries.`}
      >
        <Axes low="0 cm" high="10 cm" unit="Time" />
        {layout.alert === null ? null : (
          <line
            className={styles.chartGuide}
            data-line="alert"
            x1={layout.alert[0].x}
            y1={layout.alert[0].y}
            x2={layout.alert[1].x}
            y2={layout.alert[1].y}
          />
        )}
        {layout.action === null ? null : (
          <line
            className={styles.chartGuide}
            data-line="action"
            x1={layout.action[0].x}
            y1={layout.action[0].y}
            x2={layout.action[1].x}
            y2={layout.action[1].y}
          />
        )}
        {layout.points.length > 1 ? (
          <polyline
            className={styles.chartLine}
            points={layout.points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}
          />
        ) : null}
        {layout.points.map((p) => (
          <circle key={p.id} className={styles.chartPoint} cx={p.x} cy={p.y} r={4} />
        ))}
      </svg>
      <figcaption className={styles.meta}>{caption}</figcaption>
    </figure>
  );
}

export function GrowthChart({
  label,
  unit,
  points,
}: {
  label: string;
  unit: string;
  points: readonly { setId: string; at: string; value: number }[];
}) {
  if (points.length === 0)
    return <p className={styles.meta}>{`No ${label.toLowerCase()} recorded yet.`}</p>;
  const values = points.map((p) => p.value);
  const low = Math.min(...values);
  const high = Math.max(...values);
  const pad = high === low ? 1 : (high - low) * 0.1;
  const at = scale({
    times: points.map((p) => Date.parse(p.at)),
    low: Math.max(0, low - pad),
    high: high + pad,
    minSpanMs: 24 * HOUR_MS,
  });
  const plotted = points.map((p) => ({ id: p.setId, ...at(Date.parse(p.at), p.value) }));
  const latest = points.at(-1);
  return (
    <figure className={styles.form}>
      <svg
        viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
        className={styles.chart}
        role="img"
        aria-label={`${label}: ${String(points.length)} entries${latest === undefined ? '' : `, latest ${String(latest.value)} ${unit} on ${formatEatDateTime(new Date(latest.at))}`}.`}
      >
        <Axes
          low={`${Math.max(0, low - pad).toFixed(1)} ${unit}`}
          high={`${(high + pad).toFixed(1)} ${unit}`}
          unit="Date"
        />
        {plotted.length > 1 ? (
          <polyline
            className={styles.chartLine}
            points={plotted.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}
          />
        ) : null}
        {plotted.map((p) => (
          <circle key={p.id} className={styles.chartHollow} cx={p.x} cy={p.y} r={4} />
        ))}
      </svg>
      <figcaption className={styles.meta}>
        {`${label} as entered. Growth reference: not set up, so no percentiles are shown.`}
      </figcaption>
    </figure>
  );
}
